import { useState } from 'react';
import { Button, Spinner, Chip } from '@heroui/react';
import { Pencil, Trash2, Plus, Phone, Mail, Globe, MapPin } from 'lucide-react';
import { useCompany, useDeleteCompany, useDeleteContact } from '@/api/companies';
import { useProjects } from '@/api/projects';
import CompanyForm from './CompanyForm';
import ContactForm from './ContactForm';
import type { Contact } from '@estimator/shared';

interface CompanyDetailProps {
  companyId: string;
}

const TYPE_COLORS: Record<string, string> = {
  CONTRACTOR: '#86efac', ARCHITECT: '#a5b4fc', MANUFACTURER: '#fbbf24',
  DESIGNER: '#d8b4fe', GENERAL_CONTRACTOR: '#86efac', SUBCONTRACTOR: '#67e8f9',
  SUPPLIER: '#fca5a5', OTHER: '#94a3b8',
};

export default function CompanyDetail({ companyId }: CompanyDetailProps) {
  const { data: company, isLoading } = useCompany(companyId);
  const { data: clientProjects } = useProjects({ client_id: companyId } as any);
  const deleteCompany = useDeleteCompany();
  const deleteContact = useDeleteContact();

  const [showEditForm, setShowEditForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>();

  if (isLoading || !company) {
    return <div className="flex justify-center py-12"><Spinner /></div>;
  }

  const handleDeleteCompany = async () => {
    if (!confirm('Are you sure you want to delete this company?')) return;
    await deleteCompany.mutateAsync(companyId);
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!confirm('Delete this contact?')) return;
    await deleteContact.mutateAsync({ companyId, contactId });
  };

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            {company.name}
          </h2>
          <div className="flex gap-1.5 mt-1">
            {company.types?.map((t: string) => (
              <Chip
                key={t} size="sm"
                style={{ backgroundColor: `${TYPE_COLORS[t] ?? '#94a3b8'}22`, color: TYPE_COLORS[t] ?? '#94a3b8' }}
              >
                {t}
              </Chip>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="flat" startContent={<Pencil size={14} />} onPress={() => setShowEditForm(true)}>
            Edit
          </Button>
          <Button
            size="sm" variant="flat" color="danger" startContent={<Trash2 size={14} />}
            onPress={handleDeleteCompany}
            isDisabled={(company.project_count ?? 0) > 0}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        {company.phone && <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><Phone size={14} /> {company.phone}</div>}
        {company.email && <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><Mail size={14} /> {company.email}</div>}
        {company.website && <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><Globe size={14} /> {company.website}</div>}
        {company.address && <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><MapPin size={14} /> {company.address}</div>}
        {company.notes && <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{company.notes}</p>}
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase" style={{ color: 'var(--text-secondary)' }}>Contacts</h3>
          <Button size="sm" variant="light" startContent={<Plus size={14} />} onPress={() => { setEditingContact(undefined); setShowContactForm(true); }}>
            Add
          </Button>
        </div>
        {(company.contacts ?? []).length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No contacts yet</p>
        ) : (
          <div className="space-y-2">
            {company.contacts.map((contact: Contact) => (
              <div
                key={contact.id}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'var(--bg-surface)' }}
              >
                <div>
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{contact.name}</div>
                  <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {[contact.title, contact.email, contact.phone].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="light" isIconOnly onPress={() => { setEditingContact(contact); setShowContactForm(true); }}>
                    <Pencil size={12} />
                  </Button>
                  <Button size="sm" variant="light" color="danger" isIconOnly onPress={() => handleDeleteContact(contact.id)}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase mb-3" style={{ color: 'var(--text-secondary)' }}>Linked Projects</h3>
        {(clientProjects?.data ?? []).length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No linked projects</p>
        ) : (
          <div className="space-y-1">
            {clientProjects!.data.map((p: any) => (
              <div key={p.id} className="text-sm" style={{ color: 'var(--accent)' }}>
                {p.project_name} ({p.client_id === companyId ? 'Client' : 'Specifier'})
              </div>
            ))}
          </div>
        )}
      </div>

      <CompanyForm isOpen={showEditForm} onClose={() => setShowEditForm(false)} company={company} />
      <ContactForm isOpen={showContactForm} onClose={() => setShowContactForm(false)} companyId={companyId} contact={editingContact} />
    </div>
  );
}
